/* ============================================================
   MTi USA — Element-level scroll reveal
   Walks each Shopify section (minus header / announcement bar /
   apps) and tags meaningful content elements (cards, headings,
   paragraphs, images, CTAs) with `data-mti-reveal`. When each
   tagged element enters the viewport, `.is-revealed` is added
   and the observer stops watching it.
   ============================================================ */
(function () {
  'use strict';

  var html = document.documentElement;

  if (!('IntersectionObserver' in window)) {
    html.classList.remove('mti-reveal-ready');
    return;
  }

  // Skip these section keys entirely — they're always-on chrome.
  var SKIP_SECTION_KEYS = [
    'header', 'header-v2',
    'annbar_v2', 'annbar', 'announcement-bar', 'announcement-bar-v2',
    'apps'
  ];

  function sectionShouldSkip(sec) {
    if (sec.hasAttribute('data-mti-no-reveal')) return true;
    var id = sec.id || '';
    for (var i = 0; i < SKIP_SECTION_KEYS.length; i++) {
      if (id.indexOf('__' + SKIP_SECTION_KEYS[i]) !== -1) return true;
    }
    return false;
  }

  // Curated list of selectors for elements that should fade in.
  // Tuned to catch content units (cards, headings, paragraphs,
  // images, CTAs, stats) without over-tagging tiny inline glue.
  var REVEAL_SELECTORS = [
    // Headings
    'h1', 'h2', 'h3', 'h4',
    // Body copy (only if it has actual text, skip wrappers)
    'p',
    // Lists in editorial contexts
    'blockquote', 'figure',
    // Media
    '> img', '> picture',
    // Buttons and CTA-shaped links
    'a.button', '.button', '[class*="btn"]', '[class*="-cta"]', '[class*="__cta"]',
    // Eyebrows / pills / badges (small but meaningful)
    '[class*="eyebrow"]', '[class*="badge"]', '[class*="pill"]', '[class*="chip"]',
    // Cards / items / tiles (the big ones)
    '[class*="card"]:not([class*="cards"])',
    '[class*="__item"]', '[class*="--item"]',
    '[class*="__tile"]', '[class*="-tile"]',
    '[class*="__stat"]', '[class*="-stat"]:not([class*="status"])',
    '[class*="testimonial"]',
    '[class*="__row"]:not(tr)'
  ];

  // Build the queryable selector by joining the simple ones,
  // skipping the leading "> " ones which need a different anchor.
  var DESCENDANT_SELECTOR = REVEAL_SELECTORS
    .filter(function (s) { return s.indexOf('>') !== 0; })
    .join(',');

  // Don't double-tag: if an element has an ancestor that's already
  // tagged, skip it so cascades stagger by group rather than n^2.
  function hasTaggedAncestor(el, root) {
    var p = el.parentElement;
    while (p && p !== root) {
      if (p.hasAttribute && p.hasAttribute('data-mti-reveal')) return true;
      p = p.parentElement;
    }
    return false;
  }

  function shouldSkipElement(el) {
    if (el.hasAttribute('data-mti-no-reveal')) return true;
    // Skip empty / whitespace-only nodes — they'd reveal nothing.
    if (!el.textContent || el.textContent.trim() === '') {
      // Allow if it's media that doesn't need text.
      var tag = el.tagName;
      if (tag !== 'IMG' && tag !== 'PICTURE' && tag !== 'FIGURE') return true;
    }
    // Skip tiny inline-only elements that aren't visually distinct.
    if (el.offsetParent === null && el.tagName !== 'IMG') return true;
    return false;
  }

  // Apply stagger by index-within-parent. Same parent → cascading delay.
  function applyStagger(els) {
    var lastParent = null;
    var step = 0;
    for (var i = 0; i < els.length; i++) {
      var el = els[i];
      var p = el.parentElement;
      if (p !== lastParent) {
        step = 0;
        lastParent = p;
      } else if (step < 6) {
        step += 1;
      }
      if (step > 0 && !el.hasAttribute('data-mti-stagger')) {
        el.setAttribute('data-mti-stagger', String(step));
      }
    }
  }

  function reveal(el) {
    el.classList.add('is-revealed');
  }

  function tagSection(sec) {
    if (sectionShouldSkip(sec)) return [];
    // Tag the elements we care about inside this section.
    var nodes = sec.querySelectorAll(DESCENDANT_SELECTOR);
    var tagged = [];
    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      if (el.hasAttribute('data-mti-reveal') || el.classList.contains('is-revealed')) continue;
      if (shouldSkipElement(el)) continue;
      if (hasTaggedAncestor(el, sec)) continue;
      el.setAttribute('data-mti-reveal', '');
      tagged.push(el);
    }
    // Also catch direct child <img> / <picture> of the section.
    var directMedia = sec.querySelectorAll(':scope > img, :scope > picture, :scope > * > img, :scope > * > picture');
    for (var j = 0; j < directMedia.length; j++) {
      var m = directMedia[j];
      if (m.hasAttribute('data-mti-reveal') || m.classList.contains('is-revealed')) continue;
      if (hasTaggedAncestor(m, sec)) continue;
      m.setAttribute('data-mti-reveal', '');
      tagged.push(m);
    }
    return tagged;
  }

  // Two observers — small elements (most things) fire when 50% of
  // the element itself is in view. Tall elements (hero blocks,
  // banners, tables) can't reach 50% of their own height inside the
  // viewport, so they get a separate observer that fires when their
  // top edge has crossed the viewport's vertical midpoint instead.
  function isTall(el) {
    return el.getBoundingClientRect().height > window.innerHeight * 0.85;
  }

  var ioSmall = new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
        reveal(entry.target);
        ioSmall.unobserve(entry.target);
      }
    });
  }, {
    threshold: 0.5
  });

  // Tall-element fallback: trigger when the top half of the element
  // is past the top half of the viewport (rough "50% scrolled into
  // the upper portion of the screen").
  var ioTall = new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      if (entry.isIntersecting) {
        reveal(entry.target);
        ioTall.unobserve(entry.target);
      }
    });
  }, {
    rootMargin: '0px 0px -50% 0px',
    threshold: 0
  });

  function observe(el) {
    if (isTall(el)) ioTall.observe(el);
    else ioSmall.observe(el);
  }

  function observeAll() {
    document.querySelectorAll('[data-mti-reveal]:not(.is-revealed)').forEach(function (el) {
      // Reveal anything already in the top half of the viewport on first
      // paint — avoids a blank above-the-fold flash.
      var rect = el.getBoundingClientRect();
      var topInView = rect.top < window.innerHeight && rect.bottom > 0;
      if (topInView && rect.top < window.innerHeight * 0.55) {
        reveal(el);
        return;
      }
      observe(el);
    });
  }

  function scanAll(scope) {
    var sections = (scope || document).querySelectorAll('.shopify-section');
    var tagged = [];
    sections.forEach(function (sec) {
      var t = tagSection(sec);
      if (t.length) tagged = tagged.concat(t);
    });
    applyStagger(tagged);
    observeAll();
  }

  function safetyNet() {
    // After 4s, reveal anything still hidden — belt + braces.
    setTimeout(function () {
      document.querySelectorAll('[data-mti-reveal]:not(.is-revealed)').forEach(reveal);
    }, 4000);
  }

  function init() {
    scanAll();
    safetyNet();
    if (window.Shopify && window.Shopify.designMode) {
      document.addEventListener('shopify:section:load', function (e) {
        scanAll(e.target);
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
